import { useCallback, useEffect, useRef, useState } from "react";

import {
  COUNTDOWN_SECONDS,
  MAX_RECORDING_SECONDS,
  QUIET_PEAK,
  pickRecordingType,
  videoFileName
} from "./welcomeVideo";

export type RecorderPhase =
  | "starting"
  | "ready"
  | "blocked"
  | "unavailable"
  | "countdown"
  | "recording"
  | "recorded";

export type RecordedTake = {
  blob: Blob;
  url: string;
  fileName: string;
  seconds: number;
  quiet: boolean;
};

const stopTracks = (stream: MediaStream | null) =>
  stream?.getTracks().forEach((track) => track.stop());

/**
 * Camera, microphone and MediaRecorder for one dialog session. Everything is
 * released when `active` goes false; nothing leaves the browser until the
 * caller uploads `take.blob`.
 */
export const useWelcomeRecorder = (active: boolean) => {
  const [phase, setPhase] = useState<RecorderPhase>("starting");
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [cameras, setCameras] = useState<MediaDeviceInfo[]>([]);
  const [mics, setMics] = useState<MediaDeviceInfo[]>([]);
  const [cameraId, setCameraId] = useState("");
  const [micId, setMicId] = useState("");
  const [level, setLevel] = useState(0);
  const [countdown, setCountdown] = useState(0);
  const [elapsed, setElapsed] = useState(0);
  const [take, setTake] = useState<RecordedTake | null>(null);

  const streamRef = useRef<MediaStream | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const discardRef = useRef(false);
  const peakRef = useRef(0);
  const levelRef = useRef(0);
  const timerRef = useRef<number | null>(null);
  const takeRef = useRef<RecordedTake | null>(null);
  const activeRef = useRef(active);
  activeRef.current = active;

  const clearTimer = useCallback(() => {
    if (timerRef.current !== null) window.clearInterval(timerRef.current);
    timerRef.current = null;
  }, []);

  const replaceTake = useCallback((next: RecordedTake | null) => {
    if (takeRef.current) URL.revokeObjectURL(takeRef.current.url);
    takeRef.current = next;
    setTake(next);
  }, []);

  const open = useCallback(async (camera?: string, mic?: string) => {
    if (!navigator.mediaDevices?.getUserMedia || !("MediaRecorder" in window)) {
      setPhase("unavailable");
      return;
    }
    setPhase("starting");
    try {
      const next = await navigator.mediaDevices.getUserMedia({
        video: {
          deviceId: camera ? { exact: camera } : undefined,
          width: { ideal: 1280 },
          height: { ideal: 720 }
        },
        audio: {
          deviceId: mic ? { exact: mic } : undefined,
          echoCancellation: true,
          noiseSuppression: true
        }
      });
      if (!activeRef.current) {
        stopTracks(next);
        return;
      }
      stopTracks(streamRef.current);
      streamRef.current = next;
      setStream(next);
      const devices = await navigator.mediaDevices.enumerateDevices();
      setCameras(devices.filter((d) => d.kind === "videoinput" && d.deviceId));
      setMics(devices.filter((d) => d.kind === "audioinput" && d.deviceId));
      setCameraId(next.getVideoTracks()[0]?.getSettings().deviceId ?? "");
      setMicId(next.getAudioTracks()[0]?.getSettings().deviceId ?? "");
      setPhase("ready");
    } catch (error) {
      const name = error instanceof DOMException ? error.name : "";
      setPhase(
        name === "NotFoundError" || name === "OverconstrainedError"
          ? "unavailable"
          : "blocked"
      );
    }
  }, []);

  useEffect(() => {
    if (!active) return undefined;
    void open();
    return () => {
      clearTimer();
      discardRef.current = true;
      if (recorderRef.current?.state === "recording")
        recorderRef.current.stop();
      recorderRef.current = null;
      stopTracks(streamRef.current);
      streamRef.current = null;
      setStream(null);
      replaceTake(null);
      setElapsed(0);
      setCountdown(0);
      setPhase("starting");
    };
  }, [active, open, clearTimer, replaceTake]);

  // Level meter, sampled ten times a second.
  useEffect(() => {
    if (!stream || !stream.getAudioTracks().length) return undefined;
    const context = new AudioContext();
    const analyser = context.createAnalyser();
    analyser.fftSize = 512;
    context.createMediaStreamSource(stream).connect(analyser);
    const samples = new Uint8Array(analyser.fftSize);
    const id = window.setInterval(() => {
      analyser.getByteTimeDomainData(samples);
      let peak = 0;
      for (const s of samples) peak = Math.max(peak, Math.abs(s - 128) / 128);
      peakRef.current = Math.max(peakRef.current, peak);
      if (Math.abs(peak - levelRef.current) > 0.02) {
        levelRef.current = peak;
        setLevel(peak);
      }
    }, 100);
    return () => {
      window.clearInterval(id);
      void context.close();
    };
  }, [stream]);

  const stop = useCallback(() => {
    clearTimer();
    if (recorderRef.current?.state === "recording") recorderRef.current.stop();
  }, [clearTimer]);

  const record = useCallback(() => {
    const { current } = streamRef;
    if (!current) return;
    const type = pickRecordingType((t) => MediaRecorder.isTypeSupported(t));
    let recorder: MediaRecorder;
    try {
      recorder = new MediaRecorder(current, {
        ...(type ? { mimeType: type } : {}),
        videoBitsPerSecond: 2_500_000
      });
    } catch {
      setPhase("unavailable");
      return;
    }
    const chunks: Blob[] = [];
    const startedAt = Date.now();
    discardRef.current = false;
    peakRef.current = 0;
    recorder.ondataavailable = (e) => {
      if (e.data.size) chunks.push(e.data);
    };
    recorder.onstop = () => {
      if (discardRef.current) return;
      const blobType = recorder.mimeType || type || "video/webm";
      const blob = new Blob(chunks, { type: blobType });
      replaceTake({
        blob,
        url: URL.createObjectURL(blob),
        fileName: videoFileName(blobType),
        seconds: (Date.now() - startedAt) / 1000,
        quiet: peakRef.current < QUIET_PEAK
      });
      setPhase("recorded");
    };
    recorderRef.current = recorder;
    recorder.start(1000);
    setElapsed(0);
    setPhase("recording");
    timerRef.current = window.setInterval(() => {
      const seconds = (Date.now() - startedAt) / 1000;
      setElapsed(seconds);
      if (seconds >= MAX_RECORDING_SECONDS) stop();
    }, 250);
  }, [replaceTake, stop]);

  /** Counts down, then records. */
  const start = useCallback(() => {
    if (!streamRef.current) return;
    clearTimer();
    replaceTake(null);
    let left = COUNTDOWN_SECONDS;
    setCountdown(left);
    setPhase("countdown");
    timerRef.current = window.setInterval(() => {
      left -= 1;
      setCountdown(left);
      if (left > 0) return;
      clearTimer();
      record();
    }, 1000);
  }, [clearTimer, record, replaceTake]);

  /** Throws the take away mid-recording and counts down again. */
  const startOver = useCallback(() => {
    discardRef.current = true;
    stop();
    start();
  }, [start, stop]);

  /** Back to the live camera, without the take. */
  const retake = useCallback(() => {
    replaceTake(null);
    setElapsed(0);
    setPhase(streamRef.current ? "ready" : "starting");
  }, [replaceTake]);

  const chooseCamera = (id: string) => void open(id, micId || undefined);
  const chooseMic = (id: string) => void open(cameraId || undefined, id);

  return {
    phase,
    stream,
    cameras,
    mics,
    cameraId,
    micId,
    level,
    countdown,
    elapsed,
    take,
    retry: () => void open(),
    chooseCamera,
    chooseMic,
    start,
    stop,
    startOver,
    retake
  };
};
